import { useState, useEffect } from 'react';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { Pencil, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { createUser, addUserToSeason, removeUserFromSeason, savePredictions, getUsers, getCurrentStatus, createSeason, getSeasons } from './services/editingApi';
import { EDIT_MODE_ENABLED } from './config';

// region: API Data Structures
// These interfaces define the shape of the data received from the backend API.
// They are duplicated from the backend service for type safety in the frontend.

export interface User {
  id: number;
  name: string;
  created_at: string;
}

export interface TeamPredictionStatus {
  teamId: number;
  teamAbbreviation: string;
  teamName: string;
  conference: 'East' | 'West';
  predictedRank: number;
  actualRank: number | null;
  difference: number | null;
  deviationFromAverage: number | null;
}

export interface UserPredictionStatus {
  userId: number;
  userName: string;
  totalScore: number;
  predictions: {
    east: TeamPredictionStatus[];
    west: TeamPredictionStatus[];
  };
}

export interface TeamStanding {
  teamId: number;
  teamAbbreviation: string;
  teamName: string;
  conference: 'East' | 'West';
  rank: number;
}

export interface CurrentPredictionStatusResponse {
  season: number;
  users: UserPredictionStatus[];
  standings: {
    east: TeamStanding[];
    west: TeamStanding[];
  };
}
// endregion

// region: Helper Components
const Spinner = () => (
  <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
    <div className="animate-spin-custom rounded-full h-12 w-12 border-4 border-t-white border-white/30"></div>
  </div>
);

const getDeviationIndicator = (deviation: number | null) => {
  if (deviation === null || deviation === 0) {
    return <span className="text-slate-500">-</span>;
  }

  // Chevrons overlap inside a fixed 14x24 box so every level renders at the same height
  const ChevronStack = ({ count, direction, color }: { count: number, direction: 'up' | 'down', color: string }) => (
    <svg
      width="14"
      height="24"
      viewBox="0 0 14 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={color}
    >
      {Array.from({ length: count }).map((_, i) => {
        // Center the stack vertically: offsets span 5px per extra chevron
        const top = 10 - (count - 1) * 2.5 + i * 5;
        const points = direction === 'up'
          ? `2,${top + 4} 7,${top} 12,${top + 4}`
          : `2,${top} 7,${top + 4} 12,${top}`;
        return <polyline key={i} points={points} />;
      })}
    </svg>
  );

  if (deviation > 0) {
    if (deviation > 5) return <ChevronStack count={3} direction="up" color="text-green-400" />;
    if (deviation > 2) return <ChevronStack count={2} direction="up" color="text-green-500" />;
    return <ChevronStack count={1} direction="up" color="text-green-600" />;
  }

  if (deviation < -5) return <ChevronStack count={3} direction="down" color="text-red-400" />;
  if (deviation < -2) return <ChevronStack count={2} direction="down" color="text-red-500" />;
  return <ChevronStack count={1} direction="down" color="text-red-600" />;
};

const getRankElement = (rank: number) => {
  switch (rank) {
    case 1: return <span title="Gold Medal">🥇</span>;
    case 2: return <span title="Silver Medal">🥈</span>;
    case 3: return <span title="Bronze Medal">🥉</span>;
    default: return <span className="text-sm font-bold text-slate-400">{rank}th</span>;
  }
};
// endregion

// region: Display Components
const TeamCard = ({ team, isEditMode }: { team: TeamPredictionStatus, isEditMode: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: team.teamId });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
  };

  const diffText = team.difference !== null ? (team.difference > 0 ? `+${team.difference}` : `${team.difference}`) : null;
  const deviationTitle = team.deviationFromAverage !== null ? 
    (team.deviationFromAverage > 0 ? `+${team.deviationFromAverage.toFixed(2)}` : `${team.deviationFromAverage.toFixed(2)}`) : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 p-2 transition-all select-none
        ${isDragging 
          ? 'bg-blue-500/40 shadow-[0_0_15px_rgba(59,130,246,0.5)] scale-[1.03] z-50' 
          : `${isEditMode ? 'hover:bg-white/10' : ''}`}`}
    >
      <span className="text-[10px] font-bold w-3 text-center text-slate-500">
        {team.predictedRank}
      </span>
      <img 
        src={`/logos/${team.teamAbbreviation.toLowerCase()}.svg`} 
        alt="" 
        className="w-5 h-5 object-contain brightness-110"
      />
      <span className="text-[13px] font-medium truncate text-slate-200 flex-grow">
        {team.teamAbbreviation.toUpperCase()}
      </span>
      <div className="flex items-center gap-1">
        {diffText && <span className="text-xs font-bold text-slate-500">{diffText}</span>}
        <div className="w-4 h-6 flex items-center justify-center" title={deviationTitle}>
          {getDeviationIndicator(team.deviationFromAverage)}
        </div>
      </div>
    </div>
  );
};

const Conference = ({ title, teams, titleColor, isEditMode }: { title: string, teams: TeamPredictionStatus[], titleColor: string, isEditMode: boolean }) => (
  <div className="w-[142px]">
    <h2 className={`${titleColor} text-[11px] font-black my-4 text-center uppercase tracking-[0.2em] opacity-80`}>
      {title}
    </h2>
    <SortableContext items={teams.map(t => t.teamId)} strategy={verticalListSortingStrategy}>
      <div className="flex flex-col">
        {teams.map((team) => <TeamCard key={team.teamId} team={team} isEditMode={isEditMode} />)}
      </div>
    </SortableContext>
  </div>
);

const StandingsDisplay = ({ 
  user, 
  rank, 
  isEditMode, 
  onDelete, 
  onTeamsChange
}: { 
  user: UserPredictionStatus, 
  rank: number, 
  isEditMode: boolean, 
  onDelete: (userId: number) => void,
  onTeamsChange: (userId: number, conference: 'east' | 'west', teams: TeamPredictionStatus[]) => Promise<void>
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));
  const [isSaving, setIsSaving] = useState(false);

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    
    const isEast = user.predictions.east.some(t => t.teamId === active.id);
    const conference = isEast ? 'east' : 'west';
    const teams = isEast ? user.predictions.east : user.predictions.west;
    
    const oldIndex = teams.findIndex((t) => t.teamId === active.id);
    const newIndex = teams.findIndex((t) => t.teamId === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newTeams = arrayMove(teams, oldIndex, newIndex);
      setIsSaving(true);
      try {
        await onTeamsChange(user.userId, conference, newTeams);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const conferenceContent = (
    <>
      <Conference title="Eastern Conference" teams={user.predictions.east} titleColor="text-blue-400" isEditMode={isEditMode} />
      <Conference title="Western Conference" teams={user.predictions.west} titleColor="text-red-400" isEditMode={isEditMode} />
    </>
  );

  return (
    <div>
      <div className="flex justify-center items-center mb-4">
        <h2 className="text-white text-center text-xl font-bold">{user.userName}</h2>
        {isEditMode && (
          <div className="flex gap-2 ml-2">
            <button onClick={() => onDelete(user.userId)} className="text-red-500 hover:text-red-400" disabled={isSaving}>
              <X size={16} />
            </button>
          </div>
        )}
      </div>
      <div 
        className="relative w-fit shadow-2xl overflow-hidden flex flex-col divide-y divide-white/5"
        style={{ backgroundImage: 'linear-gradient(to bottom, #0f172a, #172554, #0f172a)' }}
      >
        {isSaving && <Spinner />}
        <div className={`flex divide-x divide-white/5 ${isSaving ? 'opacity-50 pointer-events-none' : ''}`}>
          {isEditMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              {conferenceContent}
            </DndContext>
          ) : (
            conferenceContent
          )}
        </div>
        <div className="p-2 text-center flex justify-center items-center gap-4">
          <div className="flex items-center gap-1">
            {getRankElement(rank)}
          </div>
          <span className="text-sm font-bold text-slate-300">Total Score: {user.totalScore}</span>
        </div>
      </div>
    </div>
  );
};
const StandingRow = ({ team }: { team: TeamStanding }) => (
  <div className="flex items-center gap-2 p-2 select-none">
    <span className="text-[10px] font-bold w-3 text-center text-slate-500">
      {team.rank}
    </span>
    <img
      src={`/logos/${team.teamAbbreviation.toLowerCase()}.svg`}
      alt=""
      className="w-5 h-5 object-contain brightness-110"
    />
    <span className="text-[13px] font-medium truncate text-slate-200 flex-grow" title={team.teamName}>
      {team.teamAbbreviation.toUpperCase()}
    </span>
    {/* Same height as the deviation indicator so rows line up with the prediction cards */}
    <div className="h-6" />
  </div>
);

const RealStandingsConference = ({ title, teams, titleColor }: { title: string, teams: TeamStanding[], titleColor: string }) => (
  <div className="w-[142px]">
    <h2 className={`${titleColor} text-[11px] font-black my-4 text-center uppercase tracking-[0.2em] opacity-80`}>
      {title}
    </h2>
    <div className="flex flex-col">
      {teams.length > 0
        ? teams.map((team) => <StandingRow key={team.teamId} team={team} />)
        : <p className="p-2 text-center text-xs text-slate-500">Not available yet</p>}
    </div>
  </div>
);

const RealStandingsDisplay = ({ title, standings }: { title: string, standings: CurrentPredictionStatusResponse['standings'] }) => (
  <div>
    <div className="flex justify-center items-center mb-4">
      <h2 className="text-amber-300 text-center text-xl font-bold">{title}</h2>
    </div>
    <div
      className="relative w-fit shadow-2xl overflow-hidden flex flex-col divide-y divide-white/5 ring-1 ring-amber-300/20"
      style={{ backgroundImage: 'linear-gradient(to bottom, #0f172a, #1e293b, #0f172a)' }}
    >
      <div className="flex divide-x divide-white/5">
        <RealStandingsConference title="Eastern Conference" teams={standings.east} titleColor="text-blue-400" />
        <RealStandingsConference title="Western Conference" teams={standings.west} titleColor="text-red-400" />
      </div>
    </div>
  </div>
);
// endregion

// region: Modals
const AddUserModal = ({
  isOpen,
  onClose,
  onSelectUser,
  onCreateNew,
  users,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectUser: (userId: number) => Promise<void>;
  onCreateNew: () => void;
  users: User[];
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleSelectUser = async (userId: number) => {
    setIsLoading(true);
    await onSelectUser(userId);
    setIsLoading(false);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl p-6 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-white">
          <X size={20} />
        </button>
        {isLoading && <Spinner />}
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <h3 className="text-lg font-medium text-white mb-4">Add User to Season</h3>
          <div className="flex flex-col gap-2">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => handleSelectUser(user.id)}
                className="w-full text-left px-4 py-2 rounded-md bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                {user.name}
              </button>
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <button
              onClick={onCreateNew}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 transition-colors"
            >
              Create New User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CreateUserModal = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => Promise<void>;
}) => {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setIsLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = async () => {
    if (name.trim()) {
      setIsLoading(true);
      try {
        await onConfirm(name.trim());
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl p-6 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-white">
          <X size={20} />
        </button>
        {isLoading && <Spinner />}
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <h3 className="text-lg font-medium text-white mb-4">Create New User</h3>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter user's name"
            className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 transition-colors"
              disabled={isLoading}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateSeasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  defaultYear,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (year: number) => Promise<void>;
  defaultYear: number;
}) => {
  const [year, setYear] = useState(String(defaultYear));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setYear(String(defaultYear));
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, defaultYear]);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = async () => {
    const parsedYear = parseInt(year, 10);
    if (!parsedYear) return;
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm(parsedYear);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create season');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl p-6 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-white">
          <X size={20} />
        </button>
        {isLoading && <Spinner />}
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <h3 className="text-lg font-medium text-white mb-4">Create New Season</h3>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="Enter season year"
            className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 transition-colors"
              disabled={isLoading}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteUserModal = ({
  isOpen,
  onClose,
  onConfirm,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number) => Promise<void>;
  user: UserPredictionStatus | null;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  if (!isOpen || !user) {
    return null;
  }

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm(user.userId);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl p-6 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-white">
          <X size={20} />
        </button>
        {isLoading && <Spinner />}
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <h3 className="text-lg font-medium text-white mb-4">Delete User</h3>
          <p className="text-slate-300 mb-6">Are you sure you want to delete "{user.userName}"?</p>
          <div className="flex justify-end gap-4">
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors"
              disabled={isLoading}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// endregion


export default function App() {
  const [editModeToggled, setEditModeToggled] = useState(false);
  const isEditMode = EDIT_MODE_ENABLED && editModeToggled;
  const [predictionStatus, setPredictionStatus] = useState<CurrentPredictionStatusResponse | null>(null);
  const [editedPredictions, setEditedPredictions] = useState<{[key: number]: {east: TeamPredictionStatus[], west: TeamPredictionStatus[]}}>({});
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isCreateSeasonModalOpen, setIsCreateSeasonModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [userToModify, setUserToModify] = useState<UserPredictionStatus | null>(null);
  const [seasons, setSeasons] = useState<number[]>([]);

  const fetchStatus = async (season?: number) => {
    try {
            const data: CurrentPredictionStatusResponse = await getCurrentStatus(season);
      setPredictionStatus(data);
      const initialPredictions: {[key: number]: {east: TeamPredictionStatus[], west: TeamPredictionStatus[]}} = {};
      data.users.forEach(user => {
        initialPredictions[user.userId] = user.predictions;
      });
      setEditedPredictions(initialPredictions);
    } catch (e) {
      console.error("Failed to fetch prediction status:", e);
      setError("Could not load data from the server. Is it running?");
    }
  };

  const fetchAllUsers = async () => {
    try {
      const users = await getUsers();
      setAllUsers(users);
    } catch (e) {
      console.error("Failed to fetch users:", e);
    }
  }

  const fetchSeasons = async () => {
    try {
      const data = await getSeasons();
      setSeasons([...data].sort((a, b) => a - b));
    } catch (e) {
      console.error("Failed to fetch seasons:", e);
    }
  }

  useEffect(() => {
    fetchStatus();
    fetchAllUsers();
    fetchSeasons();
  }, []);

  const handleTeamsChange = async (userId: number, conference: 'east' | 'west', teams: TeamPredictionStatus[]) => {
    if (!predictionStatus) return;
    const userPredictions = {
      ...editedPredictions[userId],
      [conference]: teams.map((team, index) => ({ ...team, predictedRank: index + 1 })),
    };
    setEditedPredictions({ ...editedPredictions, [userId]: userPredictions });

    const predictionsToSave = [
      ...userPredictions.east.map(t => ({ team_id: t.teamId, predicted_rank: t.predictedRank })),
      ...userPredictions.west.map(t => ({ team_id: t.teamId, predicted_rank: t.predictedRank })),
    ];

    try {
      await savePredictions(userId, predictionStatus.season, predictionsToSave);
    } catch (e) {
      console.error("Failed to save predictions:", e);
    } finally {
      // Refresh scores after a save, or revert to the server's order if it failed
      await fetchStatus(predictionStatus.season);
    }
  };
  
  const handleAddUser = async (name: string) => {
    if (!predictionStatus) return;
    const newUser = await createUser(name);
    await addUserToSeason(newUser.id, predictionStatus.season);
    await fetchStatus(predictionStatus.season);
    await fetchAllUsers();
    setIsCreateModalOpen(false);
  };

  const handleCreateSeason = async (year: number) => {
    await createSeason(year);
    await fetchStatus(year);
    await fetchAllUsers();
    await fetchSeasons();
    setIsCreateSeasonModalOpen(false);
  };

  const handleSelectUser = async (userId: number) => {
    if (!predictionStatus) return;
    await addUserToSeason(userId, predictionStatus.season);
    await fetchStatus(predictionStatus.season);
    await fetchAllUsers();
    setIsAddUserModalOpen(false);
  }

  const handleDeleteUser = async (userId: number) => {
    if (!predictionStatus) return;
    await removeUserFromSeason(userId, predictionStatus.season);
    await fetchStatus(predictionStatus.season);
    await fetchAllUsers();
    setIsDeleteModalOpen(false);
    setUserToModify(null);
  };

  const currentSeasonIndex = predictionStatus ? seasons.indexOf(predictionStatus.season) : -1;
  const hasPreviousSeason = currentSeasonIndex > 0;
  const hasNextSeason = currentSeasonIndex !== -1 && currentSeasonIndex < seasons.length - 1;
  // Only the latest season is still being played; older ones show their final standings
  const isCurrentSeason = currentSeasonIndex === -1 || currentSeasonIndex === seasons.length - 1;

  const goToPreviousSeason = () => {
    if (hasPreviousSeason) fetchStatus(seasons[currentSeasonIndex - 1]);
  };

  const goToNextSeason = () => {
    if (hasNextSeason) fetchStatus(seasons[currentSeasonIndex + 1]);
  };

  const openDeleteModal = (user: UserPredictionStatus) => {
    setUserToModify(user);
    setIsDeleteModalOpen(true);
  };
  
  // Calculate ranks based on the fetched and calculated scores from the backend
  const getRanks = () => {
    if (!predictionStatus || !predictionStatus.users) return new Map<string, number>();

    const sortedUsers = [...predictionStatus.users].sort((a, b) => a.totalScore - b.totalScore);
    const ranks = new Map<string, number>();
    let currentRank = 0;
    let lastScore = -Infinity;

    sortedUsers.forEach((p, index) => {
      if (p.totalScore > lastScore) {
        currentRank = index + 1;
      }
      ranks.set(p.userName, currentRank);
      lastScore = p.totalScore;
    });
    return ranks;
  };
  
  const handleAddClick = () => {
    const usersInSeason = predictionStatus?.users.map(u => u.userId) || [];
    const availableUsers = allUsers.filter(u => !usersInSeason.includes(u.id));
    if (availableUsers.length > 0) {
      setIsAddUserModalOpen(true);
    } else {
      setIsCreateModalOpen(true);
    }
  }
  
  const usersWithPredictions = predictionStatus?.users.map(user => ({
    ...user,
    predictions: editedPredictions[user.userId] || user.predictions,
  })) || [];

  const ranks = getRanks();

  if (error) {
    return <div className="min-h-screen bg-slate-950 p-6 text-center text-red-400">{error}</div>;
  }

  if (!predictionStatus) {
    return <div className="min-h-screen bg-slate-950 p-6 text-center text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {EDIT_MODE_ENABLED && (
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <label htmlFor="edit-mode" className="text-slate-400 text-sm font-medium">
            Edit Mode
          </label>
          <input
            type="checkbox"
            id="edit-mode"
            checked={isEditMode}
            onChange={() => setEditModeToggled(!editModeToggled)}
            className="relative w-10 h-5 bg-slate-700 rounded-full appearance-none cursor-pointer
              checked:bg-blue-500 transition-colors
              after:absolute after:w-4 after:h-4 after:bg-white after:rounded-full after:top-0.5 after:left-0.5
              checked:after:translate-x-[19px] after:transition-transform"
          />
        </div>
      )}
      <div className="flex justify-center items-center gap-3 mb-2">
        <button
          onClick={goToPreviousSeason}
          disabled={!hasPreviousSeason}
          className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
          aria-label="Previous season"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-center text-3xl font-bold text-white">NBA Standings {predictionStatus.season}</h1>
        <button
          onClick={goToNextSeason}
          disabled={!hasNextSeason}
          className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
          aria-label="Next season"
        >
          <ChevronRight size={24} />
        </button>
      </div>
      <p className="text-center text-slate-400 mb-8">Predictions & Real-Time Scores</p>
      {isEditMode && (
        <div className="flex justify-center mb-8">
          <button
            onClick={() => setIsCreateSeasonModalOpen(true)}
            className="px-4 py-2 rounded-md bg-slate-800 border border-white/10 text-slate-300 hover:bg-slate-700 transition-colors text-sm"
          >
            New Season
          </button>
        </div>
      )}
      <div className="flex justify-center items-start gap-8 flex-wrap">
        <RealStandingsDisplay
          title={isCurrentSeason ? 'Current Standings' : 'Final Standings'}
          standings={predictionStatus.standings}
        />
        {usersWithPredictions.map((user) => (
          <StandingsDisplay 
            key={user.userId} 
            user={user} 
            rank={ranks.get(user.userName) || 0}
            isEditMode={isEditMode}
            onDelete={() => openDeleteModal(user)}
            onTeamsChange={handleTeamsChange}
          />
        ))}
        {isEditMode && (
          <div className="h-full flex items-center mt-12">
            <button
              onClick={handleAddClick}
              className="w-10 h-10 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-slate-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        )}
      </div>
      <AddUserModal 
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSelectUser={handleSelectUser}
        onCreateNew={() => {
          setIsAddUserModalOpen(false);
          setIsCreateModalOpen(true);
        }}
        users={allUsers.filter(u => !predictionStatus.users.some(userInSeason => userInSeason.userId === u.id))}
      />
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onConfirm={handleAddUser}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToModify(null);
        }}
        onConfirm={handleDeleteUser}
        user={userToModify}
      />
      <CreateSeasonModal
        isOpen={isCreateSeasonModalOpen}
        onClose={() => setIsCreateSeasonModalOpen(false)}
        onConfirm={handleCreateSeason}
        defaultYear={predictionStatus.season + 1}
      />
    </div>
  );
}
